import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api/client";
import i18n from "@/lib/i18n/i18n";
import { API_ORIGIN } from "@/lib/config";
import { getAccessToken } from "@/lib/auth/tokens";
import type { components } from "@/lib/api/schema.gen";
import { unwrap } from "@/lib/api/unwrap";

import { expertKeys } from "./keys";
import type {
  AdminExpertDetail,
  AdminExpertRow,
  AdminInvitationsPage,
  DirectoryExpert,
  DirectoryFilters,
  ExpertDashboard,
  ExpertFavorite,
  ExpertImportReport,
  ExpertInvitation,
  ExpertMandate,
  ExpertProfileDetail,
  ExpertiseInput,
  ExpertiseState,
  GoalsState,
  InvitationValidation,
  IssueImportReport,
  RegistrationCatalogue,
  RegistrationProfile,
  ServiceRegion,
} from "../types";

/**
 * The directory endpoint accepts an `?action=<uuid>` plus repeatable étape-4
 * filter params. drf-spectacular didn't capture those on the ListAPIView, so
 * the generated query type is `never` — we cast the params to satisfy the type
 * while the real object is still serialized (arrays → repeated params) by
 * openapi-fetch at runtime.
 */
interface DirectoryQuery extends DirectoryFilters {
  action: string;
}

async function fetchDirectory(
  action: string,
  filters: DirectoryFilters,
): Promise<DirectoryExpert[]> {
  const query: DirectoryQuery = { action, ...filters };
  // Unpaginated: the endpoint returns the full ranked list (the "10 + voir
  // plus" cap is applied client-side in DirectoryView, matching the old app).
  const data = await unwrap(
    api.GET("/api/v1/experts/directory/", {
      params: { query } as unknown as { query?: never },
    }),
  );
  return data as unknown as DirectoryExpert[];
}

// --- reference data ----------------------------------------------------------

export function useServiceRegionsQuery() {
  return useQuery({
    queryKey: expertKeys.serviceRegions(),
    // Reference data — cache it hard for the session.
    staleTime: 60 * 60 * 1000,
    queryFn: () =>
      unwrap(api.GET("/api/v1/experts/service-regions/", {})) as Promise<
        ServiceRegion[]
      >,
  });
}

// --- directory ---------------------------------------------------------------

/**
 * The sectors this directory's sector facet offers: the enterprise's own, read
 * from the server so the SPA applies the same rule as the Django rail
 * (`repertoire/sector_facet.py`). Refetched every time a directory mounts
 * (`staleTime: 0`, overriding the client's 30 s): the sectors can change on
 * the profile form, in the Django app or in another tab, and none of those
 * reaches this cache. A change made while a directory is open shows on its
 * next opening, not before: the client does not refetch on window focus.
 */
export function useDirectorySectorsQuery() {
  return useQuery({
    queryKey: expertKeys.directorySectors(),
    staleTime: 0,
    queryFn: () =>
      unwrap(api.GET("/api/v1/experts/directory/sectors/", {})) as Promise<
        components["schemas"]["SectorRef"][]
      >,
  });
}

/** The organisation types the directory facet offers, labelled by the server
 *  (ExpertProfile.OrganizationType), so a reworded type reaches both apps. */
export function useDirectoryOrgTypesQuery() {
  return useQuery({
    // The labels come back in the request's language, so a language switch
    // must refetch, not serve the cached ones.
    queryKey: [...expertKeys.directoryOrgTypes(), i18n.language],
    queryFn: () =>
      unwrap(api.GET("/api/v1/experts/directory/organization-types/", {})) as Promise<
        components["schemas"]["OrganizationTypeOption"][]
      >,
  });
}

export function useDirectoryQuery(
  action: string | null,
  filters: DirectoryFilters,
) {
  return useQuery({
    queryKey: expertKeys.directory(action, filters),
    // A directory listing only makes sense for a specific piste d'action.
    enabled: Boolean(action),
    // 400/403/404 (missing action, directory disabled, action can't have
    // experts) are meaningful states the UI renders — don't retry them.
    retry: false,
    queryFn: () => fetchDirectory(action as string, filters),
  });
}

// --- public profile ----------------------------------------------------------

export function useProfileQuery(id: string | null, action?: string | null) {
  return useQuery({
    queryKey: expertKeys.profile(id ?? "", action),
    enabled: Boolean(id),
    // A missing / unauthorized / non-consenting profile 404s — a meaningful
    // state the UI renders (no retry can make it appear).
    retry: false,
    queryFn: async () =>
      (await unwrap(
        api.GET("/api/v1/experts/directory/{id}/", {
          // `action` lets the server decide `can_favorite` (the ♥), by the same
          // rule the Django profile page applies.
          params: {
            path: { id: id as string },
            ...(action ? { query: { action } } : {}),
          } as unknown as { path: { id: string } },
        }),
      )) as unknown as ExpertProfileDetail,
  });
}

// --- favorites ---------------------------------------------------------------

export function useFavoritesQuery(action?: string | null) {
  return useQuery({
    queryKey: expertKeys.favorites(action),
    retry: false,
    queryFn: async () => {
      const data = await unwrap(api.GET("/api/v1/experts/favorites/", {}));
      const results = data.results as unknown as ExpertFavorite[];
      // The list endpoint's action filter is optional server-side; scope it
      // client-side too so a per-action toggle view stays consistent.
      return action
        ? results.filter((f) => f.action === action)
        : results;
    },
  });
}

export function useAddFavorite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { action: string; expertProfileId: string }) => {
      // The request body carries only the two writable ids; the schema also
      // lists the read-only id/expert/created_at, so cast to satisfy the type.
      const body = {
        action: input.action,
        expert_profile: input.expertProfileId,
      } as components["schemas"]["ExpertFavorite"];
      return unwrap(api.POST("/api/v1/experts/favorites/", { body }));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: expertKeys.all });
    },
  });
}

export function useRemoveFavorite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (favoriteId: string) =>
      unwrap(
        api.DELETE("/api/v1/experts/favorites/{id}/", {
          params: { path: { id: favoriteId } },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: expertKeys.all });
    },
  });
}

// --- expert dashboard --------------------------------------------------------

export function useDashboardQuery() {
  return useQuery({
    queryKey: expertKeys.dashboard(),
    retry: false,
    queryFn: async () =>
      // APIView with a plain dict response — spectacular declares it empty, so
      // cast the untyped body to the known shape.
      (await unwrap(
        api.GET("/api/v1/experts/dashboard/", {}),
      )) as unknown as ExpertDashboard,
  });
}

export function useMandatesQuery(enabled = true) {
  return useQuery({
    queryKey: expertKeys.mandates(),
    enabled,
    retry: false,
    queryFn: async () => {
      const data = await unwrap(api.GET("/api/v1/experts/mandates/", {}));
      return data.results as unknown as ExpertMandate[];
    },
  });
}

// --- admin invitations -------------------------------------------------------

export function useInvitationsQuery(enabled = true) {
  return useQuery({
    queryKey: expertKeys.invitations(),
    enabled,
    retry: false,
    queryFn: async () => {
      const data = await unwrap(api.GET("/api/v1/experts/invitations/", {}));
      return data.results as unknown as ExpertInvitation[];
    },
  });
}

// --- registration wizard (the caller's own resumable draft) ------------------

export function useRegistrationQuery() {
  return useQuery({
    queryKey: expertKeys.registration(),
    retry: false,
    queryFn: async () =>
      (await unwrap(
        api.GET("/api/v1/experts/registration/", {}),
      )) as RegistrationProfile,
  });
}

export function useSaveRegistration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<RegistrationProfile>) =>
      (await unwrap(
        api.PATCH("/api/v1/experts/registration/", {
          body: patch as components["schemas"]["PatchedExpertRegistration"],
        }),
      )) as RegistrationProfile,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: expertKeys.registration() });
    },
  });
}

export function useCatalogueQuery() {
  return useQuery({
    // Its labels (the provinces among them) come back in the request's
    // language, so a language switch must refetch, not serve the cached one.
    queryKey: [...expertKeys.catalogue(), i18n.language],
    staleTime: 60 * 60 * 1000,
    queryFn: async () =>
      (await unwrap(
        api.GET("/api/v1/experts/registration/catalogue/", {}),
      )) as unknown as RegistrationCatalogue,
  });
}

export function useExpertiseQuery() {
  return useQuery({
    queryKey: expertKeys.registrationExpertise(),
    retry: false,
    queryFn: async () =>
      (await unwrap(
        api.GET("/api/v1/experts/registration/expertise/", {}),
      )) as unknown as ExpertiseState,
  });
}

export function useSaveExpertise() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (expertises: ExpertiseInput[]) =>
      requestJson<ExpertiseState>(
        "PUT",
        "/api/v1/experts/registration/expertise/",
        { expertises },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: expertKeys.all });
    },
  });
}

export function useGoalsQuery() {
  return useQuery({
    queryKey: expertKeys.registrationGoals(),
    retry: false,
    queryFn: async () =>
      (await unwrap(
        api.GET("/api/v1/experts/registration/goals/", {}),
      )) as unknown as GoalsState,
  });
}

export function useSaveGoals() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: GoalsState) =>
      requestJson<GoalsState>(
        "PUT",
        "/api/v1/experts/registration/goals/",
        payload,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: expertKeys.all });
    },
  });
}

export function useSubmitRegistration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      requestJson<RegistrationProfile>(
        "POST",
        "/api/v1/experts/registration/submit/",
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: expertKeys.all });
    },
  });
}

// --- invitation acceptance + signup (public) ---------------------------------

export function useInvitationValidate(token: string | null) {
  return useQuery({
    queryKey: expertKeys.invitationValidate(token ?? ""),
    enabled: Boolean(token),
    retry: false,
    queryFn: async () =>
      (await unwrap(
        api.GET("/api/v1/experts/invitations/{token}/validate/", {
          params: { path: { token: token as string } },
        }),
      )) as unknown as InvitationValidation,
  });
}

export interface ExpertSignupInput {
  email: string;
  first_name: string;
  last_name: string;
  password1: string;
  password2: string;
  invitation_token?: string;
}

export function useExpertSignup() {
  return useMutation({
    mutationFn: (input: ExpertSignupInput) =>
      requestJson<{ detail: string }>("POST", "/api/v1/experts/signup/", input),
  });
}

// --- CQDD admin back-office --------------------------------------------------

export function useAdminExpertsQuery(params: Record<string, string>) {
  return useQuery({
    queryKey: expertKeys.adminList(params),
    queryFn: () => {
      const search = new URLSearchParams(params).toString();
      return requestJson<{
        results: AdminExpertRow[];
        count: number;
        next: string | null;
        previous: string | null;
      }>("GET", `/api/v1/experts/admin/experts/${search ? `?${search}` : ""}`);
    },
  });
}

/** Totals for the Experts / Invitations tab labels — unfiltered, like Django's. */
export function useAdminCountsQuery() {
  return useQuery({
    queryKey: expertKeys.adminCounts(),
    queryFn: () =>
      requestJson<{ experts: number; pending_invitations: number }>(
        "GET",
        "/api/v1/experts/admin/counts/",
      ),
  });
}

export function useAdminExpertDetailQuery(id: string | null) {
  return useQuery({
    queryKey: expertKeys.adminDetail(id ?? ""),
    enabled: Boolean(id),
    retry: false,
    queryFn: async () =>
      (await unwrap(
        api.GET("/api/v1/experts/admin/experts/{id}/", {
          params: { path: { id: id as string } },
        }),
      )) as unknown as AdminExpertDetail,
  });
}

export function useReviewDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      decision: "validate" | "reject";
      // message to the expert, included in the rejection email (reject only).
      message?: string;
    }) =>
      requestJson(
        "POST",
        `/api/v1/experts/admin/experts/${input.id}/decision/`,
        {
          decision: input.decision,
          message: input.message ?? "",
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: expertKeys.admin() });
    },
  });
}

// send a follow-up ("relancer") reminder email to an expert. Sends an email
// only — it mutates nothing shown in the admin list, so no cache invalidation.
export function useRemindExpert() {
  return useMutation({
    mutationFn: (input: { id: string }) =>
      requestJson("POST", `/api/v1/experts/admin/experts/${input.id}/remind/`),
  });
}

// add a private (never-emailed) internal note to an expert candidature.
export function useAddInternalNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; body: string }) =>
      requestJson(
        "POST",
        `/api/v1/experts/admin/experts/${input.id}/notes/`,
        { body: input.body },
      ),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: expertKeys.adminDetail(input.id) });
    },
  });
}

// delete a private internal note from an expert candidature.
export function useDeleteInternalNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; noteId: string }) =>
      requestJson(
        "DELETE",
        `/api/v1/experts/admin/experts/${input.id}/notes/${input.noteId}/`,
      ),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: expertKeys.adminDetail(input.id) });
    },
  });
}

export function useAdminInvitationsQuery(params: Record<string, string>) {
  return useQuery({
    queryKey: expertKeys.adminInvitations(params),
    queryFn: () => {
      const search = new URLSearchParams(params).toString();
      return requestJson<AdminInvitationsPage>(
        "GET",
        `/api/v1/experts/admin/invitations/${search ? `?${search}` : ""}`,
      );
    },
  });
}

export function useSendInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      email: string;
      contact_name?: string;
      language: "fr" | "en";
    }) =>
      requestJson("POST", "/api/v1/experts/admin/invitations/send/", input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: expertKeys.admin() });
    },
  });
}

export function useResendInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) =>
      unwrap(
        api.POST("/api/v1/experts/admin/invitations/{invitation_id}/resend/", {
          params: { path: { invitation_id: invitationId } },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: expertKeys.admin() });
    },
  });
}

export function useRevokeInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) =>
      unwrap(
        api.POST("/api/v1/experts/admin/invitations/{invitation_id}/revoke/", {
          params: { path: { invitation_id: invitationId } },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: expertKeys.admin() });
    },
  });
}

export function useSendPendingInvites() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      unwrap(api.POST("/api/v1/experts/admin/invitations/send-pending/", {})),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: expertKeys.admin() });
    },
  });
}

// --- admin imports / export (multipart + binary; raw fetch with auth) --------

function authHeaders(): Record<string, string> {
  const token = getAccessToken();
  const headers: Record<string, string> = token
    ? { Authorization: `Bearer ${token}` }
    : {};
  // These raw fetches bypass the `api` client's middleware, which is what
  // forwards the UI language. Without it the server's validation messages come
  // back in the browser's language, so a French SPA showed English errors.
  if (i18n.language) headers["Accept-Language"] = i18n.language;
  return headers;
}

/**
 * Raw JSON request with the bearer token, for the expert APIViews whose bodies
 * drf-spectacular can't type (no ``serializer_class``): the write registration
 * steps, submit, signup, and admin decision/invite. Throws the parsed error
 * body on a non-2xx so callers can surface field errors.
 */
async function requestJson<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(`${API_ORIGIN}${path}`, {
    method,
    headers: {
      ...authHeaders(),
      ...(body === undefined ? {} : { "Content-Type": "application/json" }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = res.status === 204 ? null : await res.json().catch(() => null);
  if (!res.ok) throw data ?? new Error(`Request failed (${res.status})`);
  return data as T;
}

type ImportKind = "experts" | "issues";
type ImportReport<K extends ImportKind> = K extends "experts"
  ? ExpertImportReport
  : IssueImportReport;

async function postImport<K extends ImportKind>(
  kind: K,
  body: FormData | object,
): Promise<ImportReport<K>> {
  const isForm = body instanceof FormData;
  const res = await fetch(
    `${API_ORIGIN}/api/v1/experts/admin/import/${kind}/`,
    {
      method: "POST",
      headers: {
        ...authHeaders(),
        ...(isForm ? {} : { "Content-Type": "application/json" }),
      },
      body: isForm ? body : JSON.stringify(body),
    },
  );
  const data = await res.json();
  if (!res.ok) throw data;
  return data as ImportReport<K>;
}

export function useImportPreview<K extends ImportKind>(kind: K) {
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append("file", file);
      return postImport(kind, form);
    },
  });
}

export function useImportConfirm<K extends ImportKind>(kind: K) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (token: string) => postImport(kind, { token }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: expertKeys.admin() });
    },
  });
}

/** Trigger a browser download of an admin .xlsx (export / template / errors). */
export async function downloadExpertXlsx(
  path: string,
  filename: string,
): Promise<void> {
  const res = await fetch(`${API_ORIGIN}${path}`, { headers: authHeaders() });
  if (!res.ok) throw new Error("Download failed");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
